import { getApiBaseUrl } from '@/lib/api'
import type { Worker } from '@/types'

const resolveWorkerPhotoUrl = (url?: string | null) => {
  if (!url) {
    return undefined
  }

  const trimmed = url.trim()
  if (!trimmed) {
    return undefined
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }

  const base = getApiBaseUrl().replace(/\/+$/, '')
  if (trimmed.startsWith('/')) {
    return `${base}${trimmed}`
  }

  return `${base}/${trimmed.replace(/^\//, '')}`
}

export function getWorkerDisplayName(worker: Worker) {
  return worker.display_name || worker.full_name || worker.user_registration_number || `Worker ${worker.id}`
}

export function getWorkerInitials(name?: string | null) {
  if (!name || !name.trim()) return '??'

  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function getWorkerAvatarUrl(worker: Worker) {
  const workerWithPhoto = worker as Worker & { photo_url?: string | null }
  const raw = worker.avatar || worker.avatar_url || workerWithPhoto.photo_url || undefined
  const resolved = resolveWorkerPhotoUrl(raw)
  return resolved || undefined
}

export function getWorkerLinkedPostLabel(worker: Worker) {
  const direct = String(worker.linked_post_label ?? '').trim()
  if (direct) {
    return direct.split(/\s+·\s+/).pop()?.trim() || direct
  }

  const roleName = String(worker.linked_post_role_name ?? '').trim()
  return roleName || null
}

export function normalizeWorkerRegistrationStatus(registration_status: number | string | undefined | null) {
  const status = Number(registration_status ?? -1)
  if (status === 0) return 'approved'
  if (status === 1) return 'pending'
  if (status === 2) return 'rejected'
  if (status === 3) return 'unregistered'
  if (status === 4) return 'suspended'
  if (status === 5) return 'resigned'
  return 'unknown'
}

export function getWorkerStatusMeta(worker: Worker) {
  const normalized = normalizeWorkerRegistrationStatus(worker.registration_status)

  if (normalized === 'pending') {
    return {
      value: normalized,
      label: 'Pending Approval',
      className: 'border-amber-200 bg-amber-50 text-amber-700',
      accent: 'from-amber-400 to-orange-400',
    }
  }

  if (normalized === 'approved') {
    return {
      value: normalized,
      label: 'Approved',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      accent: 'from-emerald-400 to-teal-500',
    }
  }

  if (normalized === 'suspended') {
    return {
      value: normalized,
      label: 'Suspended',
      className: 'border-violet-200 bg-violet-50 text-violet-700',
      accent: 'from-violet-400 to-purple-500',
    }
  }

  if (normalized === 'rejected') {
    return {
      value: normalized,
      label: 'Rejected',
      className: 'border-rose-200 bg-rose-50 text-rose-700',
      accent: 'from-rose-400 to-red-500',
    }
  }

  if (normalized === 'resigned') {
    return {
      value: normalized,
      label: 'Resigned',
      className: 'border-zinc-300 bg-zinc-100 text-zinc-700',
      accent: 'from-zinc-400 to-slate-400',
    }
  }

  if (normalized === 'unregistered') {
    return {
      value: normalized,
      label: 'Unregistered',
      className: 'border-slate-200 bg-slate-50 text-slate-700',
      accent: 'from-slate-300 to-slate-400',
    }
  }

  return {
    value: normalized,
    label: String(normalized ?? 'Unknown').trim() || 'Unknown',
    className: 'border-slate-200 bg-slate-50 text-slate-700',
    accent: 'from-slate-300 to-slate-400',
  }
}
